<template>
  <EDropdown
    v-bind="{ visible, 'data-test-id': 'dropdown-wrapper', ...$attrs }"
    :class="classes"
    :aria-hidden="!visible"
    @click="onClose"
  >
    <section
      class="modal-dropdown__content"
      @click.stop="() => {}"
      @keyup.escape="onClose"
    >
      <slot />
      <footer class="modal-dropdown__footer" data-test-id="modal-dropdown-footer">
        {{ title }}
        <div class="modal-dropdown__close">
          <EIconButton
            icon="close"
            size="xs"
            icon-size="s"
            data-test-id="close-button"
            @click.stop="onClose"
          />
        </div>
      </footer>
    </section>
  </EDropdown>
</template>

<script>
import { focusFocusableChild } from '~~/common/js/client/keyboard-utils';

import classes from '~~/common/vue/mixins/classes';
import EDropdown from '~~/common/vue/components/dropdown';
import EIconButton from '~~/common/vue/components/icon-button';

export const sizes = ['m', 'l'];
/**
 * Like Dropdown, but looks like a modal on mobile
 */
export default {
  name: 'EModalDropdown',
  components: {
    EDropdown,
    EIconButton,
  },
  mixins: [classes],
  props: {
    title: {
      type: String,
      required: true,
    },
    visible: {
      type: Boolean,
      default: false,
    },
    /**
     * one of `m`, `l`
     */
    size: {
      cssClass: true,
      type: String,
      default: sizes[0],
      validator: (size) => sizes.includes(size),
    },
    footerAlwaysShown: {
      cssClass: true,
      type: Boolean,
      default: false,
    },
  },
  watch: {
    visible(value) {
      // When the modal is shown, we want a fixed body
      // so the body won't scroll
      // When the modal is hidden,
      // we want to remain at the top of the scroll position
      document.body.classList.toggle('no-scroll--desktop-down', value);
      if (value) {
        // defer to next event loop cycle, because Vue won't have
        // rendered the children at this point. `$nextTick` didn't
        // seem to work in this case.
        setTimeout(() => focusFocusableChild(this.$el), 0);
      }
    },
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
$sizes: (
  m: (
    default: (
      min-width: 300px,
      max-width: 300px,
    ),
    desktop: (
      min-width: 140px,
      max-width: 180px,
    ),
  ),
  l: (
    default: (
      min-width: 300px,
      max-width: 300px,
    ),
    desktop: (
      min-width: 260px,
      max-width: none,
    ),
  ),
);

.modal-dropdown {
  min-width: 0;

  @include desktop-down {
    animation: e-fade-in $timing-1s forwards;
    align-items: center;
    background: $gray-70-40;
    box-shadow: none;
    display: flex;
    height: 100%;
    justify-content: center;
    left: 0;
    position: fixed;
    top: 0;
    width: 100%;
    z-index: $z-index-overlay;
  }
}

.modal-dropdown__content {
  display: flex;
  flex-direction: column;

  @include desktop-down {
    @include elevation(2);

    animation: e-slide-top $timing-1s forwards;
    background: $color-background-primary;
    border-radius: $border-radius-s;
    margin: $space-l;
  }
}

@each $size, $config in $sizes {
  .modal-dropdown--size-#{$size} {
    .modal-dropdown__content {
      min-width: map-get(map-get($config, 'default'), 'min-width');
      max-width: map-get(map-get($config, 'default'), 'max-width');

      @include desktop {
        min-width: map-get(map-get($config, 'desktop'), 'min-width');
        max-width: map-get(map-get($config, 'desktop'), 'max-width');
      }
    }
  }
}

.modal-dropdown__close {
  display: inherit;
}

.modal-dropdown__footer {
  @include divider('bottom');

  // special value to accomodate the icon-button in xs size
  $padding-vertical: 8px;

  align-items: center;
  display: flex;
  font-size: $font-m;
  justify-content: space-between;
  max-height: $space-3l;
  order: -1;
  padding: $padding-vertical $space-m;

  @include desktop {
    display: none;

    .modal-dropdown--footer-always-shown & {
      display: flex;

      .modal-dropdown__close {
        display: none;
      }
    }
  }
}
</style>
